/**
 * Job Log — ชั้น API ของหน้า /JobLog (ดูและสั่งงาน Hangfire ของ process ที่ SPA คุยด้วย)
 *
 * ครอบ `api/v1/JobLog/*` · แผนงาน: tasks/2026-09-18-hangfire-job-log/TASK.md (หัวข้อ API contract)
 * response ทุกตัวห่อด้วย ResponseModel `{ message, status, data }` ฟังก์ชันข้างล่างแกะ `.data` ให้แล้ว
 *
 * ⚠️ ห้ามเปลี่ยนไปใช้ `$call` — หน้านี้ poll ทุก 5 วินาที และ apiGetAuth เด้ง Swal ทุกครั้งที่ error
 *    API ล่มหนึ่งนาทีจะได้ Swal ซ้อนกัน 12 อัน · ที่นี่จึงโยน error ที่มี `httpStatus` ติดมา
 *    แล้วให้หน้าจอเป็นคนตัดสินใจว่าจะเล่ายังไง (แบบเดียวกับ services/qaConsole/qaConsoleApi.js)
 *
 * ⚠️ axios instance แยกของตัวเอง — interceptor ที่ลงทะเบียนบน axios ตัว default จะไม่มาเกี่ยว
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/** APIStatus.NotFound — ใช้แยก "ไม่เจองาน" ออกจาก "ไม่มี endpoint นี้" ตอนได้ 404 */
export const API_STATUS_NOT_FOUND = 6;

/**
 * ดัก "route ไม่มีอยู่จริงบน API ตัวที่คุยด้วย"
 *
 * ⚠️ SPA fallback ของ server ตอบ **200 + index.html** ให้ path ที่ไม่ตรง route ไหนเลย
 *    ถ้าไม่ดักตรงนี้ HTML จะไหลลงไปเป็น "ข้อมูล" แล้วไปพังตอน render แทนที่จะบอกความจริงว่า
 *    API ที่ deploy อยู่ยังไม่มีฟีเจอร์นี้
 */
function assertNotSpaFallback(response) {
  const headers = (response && response.headers) || {};
  const contentType = String(headers['content-type'] || headers['Content-Type'] || '');
  const body = response && response.data;
  const looksLikeHtml =
    contentType.includes('text/html') || (typeof body === 'string' && body.trimStart().startsWith('<'));
  if (!looksLikeHtml) return;

  const err = new Error('API returned a web page instead of data — this endpoint does not exist on the API being called');
  err.httpStatus = response && response.status;
  err.isRouteMissing = true;
  err.isUnwrapped = true;
  throw err;
}

function unwrap(response) {
  assertNotSpaFallback(response);
  const body = response && response.data;
  if (body && typeof body === 'object' && 'status' in body) {
    if (body.status === API_STATUS_SUCCESS) return body.data;
    const err = new Error(body.message || 'Request failed');
    err.status = body.status;
    err.httpStatus = response.status;
    err.isUnwrapped = true;
    throw err;
  }
  return body;
}

function toError(error) {
  // ⚠️ error ที่เราสร้างเองใน unwrap ไม่มี `response` — ห่อซ้ำแล้วธง isRouteMissing / status จะหายไป
  if (error && error.isUnwrapped) return error;

  const body = error && error.response && error.response.data;
  const bodyIsObject = body && typeof body === 'object';
  const err = new Error(
    (bodyIsObject && (body.message || body.Message)) || (error && error.message) || 'Request failed'
  );
  err.httpStatus = error && error.response && error.response.status;
  err.status = bodyIsObject ? body.status ?? body.Status : undefined;
  return err;
}

async function get(url, params) {
  try {
    return unwrap(await http.get(url, params ? { params } : undefined));
  } catch (e) {
    throw toError(e);
  }
}

async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload || {}));
  } catch (e) {
    throw toError(e);
  }
}

const ROOT = 'api/v1/JobLog';

/** สรุปทั้งหน้า: counts, servers, queues, recurring, hangfireEnabled, instance */
export function getOverview() {
  return get(`${ROOT}/overview`);
}

/**
 * รายการงานของ state หนึ่ง (Processing | Enqueued | Scheduled | Succeeded | Failed | Deleted)
 * เรียงเวลาใหม่สุดก่อน · `total` = จำนวนหลังกรอง
 */
export function getJobs({ state, from = 0, count = 25, search = '', hideEveryMinute = true } = {}) {
  const params = { state, from, count, hideEveryMinute: Boolean(hideEveryMinute) };
  const q = typeof search === 'string' ? search.trim() : '';
  if (q) params.search = q;
  return get(`${ROOT}/jobs`, params);
}

/** รายละเอียดงานหนึ่งตัว + ประวัติ state ใหม่สุดก่อน · ไม่เจอ = error ที่ httpStatus 404 / status 6 */
export function getJob(jobId) {
  return get(`${ROOT}/jobs/${encodeURIComponent(jobId)}`);
}

/** ส่งเข้าคิวใหม่ — server อนุญาตเมื่อ state ∈ Failed, Succeeded, Deleted, Scheduled */
export function requeueJob(jobId) {
  return post(`${ROOT}/jobs/${encodeURIComponent(jobId)}/requeue`);
}

/** ลบ — server อนุญาตเมื่อ state ∈ Enqueued, Scheduled, Failed (งานที่กำลังรันหยุดไม่ได้) */
export function deleteJob(jobId) {
  return post(`${ROOT}/jobs/${encodeURIComponent(jobId)}/delete`);
}

/** สั่ง recurring job ให้สร้างงานเข้าคิวเดี๋ยวนี้ (ไม่กระทบรอบตามตาราง) */
export function triggerRecurring(recurringJobId) {
  return post(`${ROOT}/recurring/${encodeURIComponent(recurringJobId)}/trigger`);
}

export default {
  getOverview,
  getJobs,
  getJob,
  requeueJob,
  deleteJob,
  triggerRecurring,
};
